import { renderCustomFieldValue } from "@/helpers/renderCustomeFieldValue";
import { transformSmartSignals } from "@/helpers/transformSmartSignals";
import type { IEventDetailsResponse } from "../interfaces/events";
import type {
	DeviceCustomFields,
	IExtraInfo,
	IInfo,
} from "../interfaces/shared";
import { runtimeConfig, runtimeConfigIssues } from "./runtime-config";

// Type for device info data parameter
interface DeviceInfoData {
	type?: string;
	id?: string;
	reference?: string;
	browser?: string;
	operating_system?: string;
	os?: string;
	ip_address?: string;
	connected_customers?: string[];
	last_seen?: string;
	custom_fields?: DeviceCustomFields[];
}

export const INDIVIDUAL = "individual";
export const FRAUD_TITLE = "Fraud";
export const SELECT_OPTION_LABEL = "Select...";
export const NODE_ENV = process.env.NODE_ENV as string;
export const IS_DEVELOPMENT =
	["development", "localdev", "localtilt"].includes(NODE_ENV) ||
	location.origin.includes("localhost");
export const IS_PRODUCTION = NODE_ENV === "production";
/** Deployment name (staging, preprod, production, ...). `NODE_ENV` is "production" in every build. */
export const APP_ENV = process.env.APP_ENV as string;
// Values a deployment can set in `config.js` read it first and fall back to
// the build's .env, so SaaS builds behave as before when it is empty.
export const API_URL = process.env.VITE_APP_API_URL as string;
export const API_URL_V2 =
	runtimeConfig.apiUrl ?? (process.env.VITE_APP_API_URL_V2 as string);
export const KEYCLOAK_URL =
	runtimeConfig.keycloak?.url ?? (process.env.VITE_APP_KEYCLOAK_URL as string);
export const KEYCLOAK_CLIENT_ID =
	runtimeConfig.keycloak?.clientId ??
	(process.env.VITE_APP_KEYCLOAK_CLIENT_ID as string);
export const KEYCLOAK_REALM =
	runtimeConfig.keycloak?.realm ??
	(process.env.VITE_APP_KEYCLOAK_REALM as string);
export const FLIPT_URL =
	runtimeConfig.flipt?.url ?? (process.env.VITE_APP_FLIPT_URL as string);
export const FLIPT_NAMESPACE =
	runtimeConfig.flipt?.namespace ??
	(process.env.VITE_APP_FLIPT_NAMESPACE as string);
export const FOCAL_URL = process.env.VITE_APP_FOCAL_URL as string;
// Read through import.meta.env so the bundler can drop SaaS-only branches
// from the self-hosted build.
export const IS_SELF_HOSTED = import.meta.env.VITE_APP_SELF_HOSTED === "true";
export const IS_POC = process.env.VITE_APP_POC === "true";
export const SENTRY_DNS = IS_SELF_HOSTED
	? ""
	: process.env.VITE_APP_SENTRY_DSN ||
		"https://035330a9e9da4aadbfc81bed88e7c81d@o287528.ingest.sentry.io/4506171647393792";
// Empty disables Microsoft Clarity for the build — see src/services/clarity.
export const CLARITY_PROJECT_ID = IS_SELF_HOSTED
	? ""
	: process.env.VITE_APP_CLARITY_PROJECT_ID || "";
// Empty / "0" disables Hotjar for the build — see src/services/hotjar.
export const HOTJAR_SITE_ID = IS_SELF_HOSTED
	? 0
	: Number(process.env.VITE_APP_HOTJAR_SITE_ID || 0);
// Empty disables Userpilot for the build — see src/services/userpilot.
export const USERPILOT_TOKEN = IS_SELF_HOSTED
	? ""
	: process.env.VITE_APP_USERPILOT_TOKEN || "";
// Optional: linked from the cookie consent banner when set.
export const PRIVACY_POLICY_URL = process.env.VITE_APP_PRIVACY_POLICY_URL || "";
export const BROWSER_FINGERPRINT_SDK_API_KEY =
	process.env.VITE_APP_BROWSER_FINGERPRINT_SDK_API_KEY || "";
export const BROWSER_FINGERPRINT_SDK_API_URL =
	process.env.VITE_APP_BROWSER_FINGERPRINT_SDK_API_URL;
// Leaflet tile URL template. Self-hosted has no public fallback: empty shows the
// map's "not available" note instead of calling the public OpenStreetMap servers.
export const MAP_TILES_URL =
	runtimeConfig.mapTilesUrl ??
	(process.env.VITE_APP_MAP_TILES_URL ||
		(IS_SELF_HOSTED
			? ""
			: "https://{s}.tile.openstreetmap.de/tiles/osmde/{z}/{x}/{y}.png"));

const REQUIRED_CONFIG = {
	apiUrl: API_URL_V2,
	"keycloak.url": KEYCLOAK_URL,
	"keycloak.realm": KEYCLOAK_REALM,
	"keycloak.clientId": KEYCLOAK_CLIENT_ID,
	"flipt.url": FLIPT_URL,
};

// Non-empty means the app can't start: index.tsx shows them instead of logging in.
export const CONFIG_ERRORS: string[] = [
	...runtimeConfigIssues,
	...Object.entries(REQUIRED_CONFIG)
		.filter(([, value]) => !value)
		.map(([key]) => `${key}: Required`),
];

export const DASHBOARD = "Dashboard";
export const EVENTS = "Events";
export const SESSIONS = "Sessions";
export const CUSTOMERS = "Customers";
export const DEVICES = "Devices";
export const LISTS = "Lists";
export const CUSTOM_FIELDS = "Fields";
export const RULES = "Rules";
export const ACCOUNT_SETTINGS = "Account Settings";
export const TEAMS_MEMBERS = "Teams and Members";
export const LOGS = "Logs";
export const API_KEYS = "API Keys";
export const SETTINGS = "Settings";
export const WEBHOOKS = "Webhooks";

export const generalInfo: (data: IEventDetailsResponse) => IInfo[] = (data) => [
	{
		title: "Customer ID",
		contant: {
			text: data.customer_id,
			hyperlink: true,
		},
	},
	{
		title: "Event ID",
		contant: {
			text: data.id,
		},
	},
	{
		title: "Score",
		contant: {
			score: data.score,
			hint: "Event's total score is the sum of all Rule Scores found below.",
		},
	},
	{
		title: "State",
		contant: {
			state: data.state,
			hint: "Event's state is auto generated based on pre-defined auto-decisioning thresholds. Thresholds can be controlled in settings.",
		},
	},
	{
		title: "Date",
		contant: {
			date: data.timestamp,
		},
	},
	{
		title: "Time",
		contant: {
			time: data.timestamp,
		},
	},
	{
		title: "Event type",
		contant: {
			text: data.event_type,
		},
	},
	{
		title: "GPS Location",
		contant: {
			text: data.location,
		},
	},
	{
		title: "IP Location",
		contant: {
			text: data.ip_location,
		},
	},
	{
		title: "IP Address",
		contant: {
			text: data.ip_address,
		},
	},
];
export const devicesInfo: (
	data: DeviceInfoData,
	hyperlink: boolean,
) => (IInfo | IExtraInfo)[] = (data, hyperlink) => {
	const tenantCustomFields = data?.custom_fields
		?.filter(({ origin }: DeviceCustomFields) => origin === "tenant")
		.map((field: DeviceCustomFields) => ({
			title: field.label,
			contant: {
				text: renderCustomFieldValue(field.value, field.data_type),
			},
		}));
	const _smartSignals = transformSmartSignals(data?.custom_fields);

	return [
		{
			title: "Device Type",
			contant: {
				text: data.type,
			},
		},
		{
			title: "Device ID",
			contant: {
				text: data.reference || data.id,
				hyperlink: hyperlink,
			},
		},
		{
			title: "Browser",
			contant: {
				text: data.browser,
			},
		},
		{
			title: "Operating System",
			contant: {
				text: data.os,
			},
		},
		{
			title: "Other Connected Customers",
			contant: {
				dropDown: {
					listTitle: "Associated Customers",
					listItems: data.connected_customers || [],
				},
				hint: "Focal runs network analysis that connects accounts that have been accessed using the same devices.",
			},
		},
		{ last_seen: data.last_seen },
		{ smartSignals: _smartSignals },
		...(tenantCustomFields || []),
	];
};

export const THRESHOLD_SCORES = {
	low: 30,
	mid: 70,
};

export const CALENDAR_TYPES = {
	GREGORIAN: "gregorian",
	HIJRI: "hijri",
};

export const eventTableHeaders = [
	{
		key: "reference",
		label: "Event ID",
	},
	{
		key: "customer_reference",
		label: "Customer ID",
	},
	{
		key: "score",
		label: "Score",
	},
	{
		key: "state",
		label: "State",
	},
	{
		key: "date",
		label: "Date",
	},
	{
		key: "event_type",
		label: "Event type",
	},
];

export const deviceTableHeaders = [
	{
		key: "reference",
		label: "Device ID",
	},
	{
		key: "type",
		label: "Device Type",
	},
	{
		key: "browser",
		label: "Browser",
	},
	{
		key: "number_of_connected_customers",
		label: "Count of Connected Customers",
	},
	{
		key: "os",
		label: "Operating System",
	},
	{
		key: "associated_events",
		label: "Associated Events",
	},
];

export const customerTableHeaders = [
	{
		key: "reference",
		label: "Customer ID",
	},
	{
		key: "username",
		label: "User Name",
	},
	{
		key: "number_of_rejected_events",
		label: "Reject Events",
	},
	{
		key: "number_of_reviewed_events",
		label: "Review Events",
	},
	{
		key: "number_of_approved_events",
		label: "Approve Events",
	},
];

export const eventsTableHeaders = [
	{
		key: "pin",
		label: "",
	},
	{
		key: "reference",
		label: "Event ID",
	},
	{
		key: "score",
		label: "Score",
	},
	{
		key: "state",
		label: "State",
	},
	{
		key: "date",
		label: "Date",
	},
	{
		key: "event_type",
		label: "Event type",
	},
];

export const devices_eventsTableHeaders = [
	{
		key: "pin",
		label: "",
	},
	{
		key: "customer_reference",
		label: "Customer ID",
	},
	{
		key: "reference",
		label: "Event ID",
	},
	{
		key: "score",
		label: "Score",
	},
	{
		key: "state",
		label: "State",
	},
	{
		key: "date",
		label: "Date",
	},
	{
		key: "event_type",
		label: "Event type",
	},
];

export const devicesTableHeaders = [
	{
		key: "type",
		label: "Device Type",
	},
	{
		key: "reference",
		label: "Device ID",
	},
];

export const GROUP_BY = {
	DAY: "day",
	WEEK: "week",
	MONTH: "month",
};

export const ROLES = {
	ADMIN: "Admin",
} as const;

export const STATUS_COLOR = {
	REVIEW: "#EBB228",
	APPROVE: "hsl(142, 72%, 29%)",
	REJECT: "#F23C3C",
} as const;

export const DATE_FORMAT = "dd MMM yyyy";
export const TIME_FORMAT = "HH:mm:ss a";

export const USER_SUBSCRIPTIONS_OPTIONS = [
	{ value: "device-risk", label: "Device Intelligence" },
	{ value: "financial-risk", label: "Payment Intelligence" },
	{ value: "device-fingerprint", label: "Device Fingerprint" },
	{ value: "customer-risk", label: "Customer Risk" },
] as const;

export const USER_SUBSCRIPTIONS: string[] = USER_SUBSCRIPTIONS_OPTIONS.map(
	(sub) => sub.value,
);

// Subscriptions that are selectable as a *rule tag* (the event-domain the rule
// covers). Customer-risk / device-fingerprint are intentionally excluded from
// the Rule Tag picker — the scored-vs-customer-risk choice is made later via
// the Score Type selector on the Configure & Create step.
export const RULE_TAG_SUBSCRIPTION_VALUES = [
	"device-risk",
	"financial-risk",
] as const;

export const FLIPT_FLAGS = {
	"multi-language-support": "multi-language-support",
	"browser-fingerprint-sdk": "browser-fingerprint-sdk",
	"enable-trigger-FP-sdk-on-action": "enable-trigger-FP-sdk-on-action",
	"webhooks-integration": "webhooks-integration",
	"API-Keys-UI-Changes": "API-Keys-UI-Changes",
	"ai-rule-builder": "ai-rule-builder",
	"ai-rule-builder-action-types": "ai-rule-builder-action-types",
	"financial-events-report": "financial-events-report",
	"unified-events-dashboard": "unified-events-dashboard",
	"fraud-dashboard-enhancements": "fraud-dashboard-enhancements",
	"fusion-strategy-enabled": "fusion-strategy-enabled",
	"customer-risk-rules-update": "customer-risk-rules-update",
	"onhold-enabled": "onhold-enabled",
	"behavioral-biometrics-pages": "behavioral-biometrics-pages",
	"financial-fraud-cards": "financial-fraud-cards",
	"customer-page-enhancements": "customer-page-enhancements",
	"sessions-date-filter": "sessions-date-filter",
	"sessions-status-filter-enabled": "sessions-status-filter-enabled",
	"session-pages-with-mock-data": "session-pages-with-mock-data",
	"customer-transactions-amount-by-channel":
		"customer-transactions-amount-by-channel",
	"enable-cookie-consent": "enable-cookie-consent",
	...Object.fromEntries(USER_SUBSCRIPTIONS.map((key) => [key, key])),
} as const;

// Reported off in self-hosted builds whatever Flipt says: both hand off to Focal, which isn't
// deployed alongside a self-hosted install.
export const SELF_HOSTED_DISABLED_FLAGS: ReadonlySet<string> = new Set([
	FLIPT_FLAGS["onhold-enabled"],
	FLIPT_FLAGS["webhooks-integration"],
]);

export const customerEventsCSVHeaders = [
	{
		key: "reference",
		label: "Event ID",
	},
	{
		key: "score",
		label: "Score",
	},
	{
		key: "state",
		label: "State",
	},
	{
		key: "date",
		label: "Date",
	},
	{
		key: "time",
		label: "Time",
	},
	{
		key: "event_type",
		label: "Event type",
	},
	{
		key: "country",
		label: "country",
	},
	{
		key: "country_code",
		label: "country code",
	},
	{
		key: "city",
		label: "city",
	},
	{
		key: "latitude",
		label: "Latitude",
	},
	{
		key: "longitude",
		label: "Longitude",
	},
	{
		key: "ip",
		label: "IP Address",
	},
	{
		key: "is_vpn",
		label: "VPN",
	},
	{
		key: "is_hosting",
		label: "Hosting",
	},
	{
		key: "isp",
		label: "ISP",
	},
];

export const ENTITY_TYPES: Record<string, string> = {
	nationality: "Nationality",
	customer_id: "Customer ID",
	device_id: "Device ID",
	country: "Country",
	ip_address: "IP Address",
	location: "Location Cell",
	phone_number: "Phone Number",
	email: "Email",
	swift_code: "SWIFT Code",
	counterparty_iban: "Counterparty IBAN",
	counterparty_account_number: "Counterparty Account Number",
	currency: "Currency",
	mcc: "MCC",
	merchant_acquirer_id: "Merchant Acquirer Id",
	merchant_issuer_id: "Merchant Issuer Id",
	merchant_name: "Merchant Name",
	transaction_country: "Transaction Country",
} as const;
